// Reviews Service
import { CreateOrUpdateReview, AsyncGetGameReviews, DeleteReview, AsyncGetGameReviewDistribution, AsyncGetTopReviewedGames } from './api';
import { Api } from '@infinitro/global';
// Flag to switch between mock and real API
const USE_MOCK = false;
// ============================================================================
// MOCK IMPLEMENTATIONS (for testing)
// ============================================================================
const mockReviews = [
    {
        reviewId: '1',
        playerId: 'player1',
        username: 'Buddy20',
        rating: 10,
        reviewText: 'This game is absolutely amazing! Got into the bonus round and won big.',
        createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: undefined
    },
    {
        reviewId: '2',
        playerId: 'player2',
        username: 'GameMaster',
        rating: 9,
        reviewText: 'One of the best slots I have ever played. The multipliers are fantastic and the RTP is very fair.',
        createdAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: undefined
    }
];
async function mockCreateOrUpdateReview(input) {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {
        success: true,
        reviewId: `review-${Date.now()}`,
        message: 'Review created successfully'
    };
}
async function mockGetGameReviews(gameSlug, providerSlug, limit, offset) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return mockReviews.slice(offset || 0, limit ? (offset || 0) + limit : undefined);
}
async function mockDeleteReview(reviewId) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return {
        success: true,
        message: 'Review deleted successfully'
    };
}
async function mockGetGameReviewDistribution(gameSlug, providerSlug) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return [
        { starRating: '10', reviewCount: 45, percentage: '30.00' },
        { starRating: '9', reviewCount: 30, percentage: '20.00' },
        { starRating: '8', reviewCount: 25, percentage: '16.67' },
        { starRating: '7', reviewCount: 20, percentage: '13.33' },
        { starRating: '6', reviewCount: 15, percentage: '10.00' },
        { starRating: '5', reviewCount: 10, percentage: '6.67' },
        { starRating: '4', reviewCount: 3, percentage: '2.00' },
        { starRating: '3', reviewCount: 1, percentage: '0.67' },
        { starRating: '2', reviewCount: 1, percentage: '0.67' },
        { starRating: '1', reviewCount: 0, percentage: '0.00' },
        { starRating: '0', reviewCount: 0, percentage: '0.00' }
    ];
}
async function mockGetTopReviewedGames(minRating, limit) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return [
        {
            gameSlug: 'starburst',
            providerSlug: 'netent',
            averageRating: '9.5',
            totalReviews: 150
        },
        {
            gameSlug: 'book-of-dead',
            providerSlug: 'playngo',
            averageRating: '9.2',
            totalReviews: 120
        }
    ];
}
// ============================================================================
// API IMPLEMENTATIONS
// ============================================================================
async function apiCreateOrUpdateReview(input) {
    const result = await CreateOrUpdateReview({
        variables: input,
        context: { api: Api.player } // Requires authentication
    });
    if (!result.data?.createOrUpdateReview) {
        throw new Error('Failed to create or update review');
    }
    return {
        success: result.data.createOrUpdateReview.success,
        reviewId: result.data.createOrUpdateReview.reviewId || '',
        message: result.data.createOrUpdateReview.message || ''
    };
}
async function apiGetGameReviews(gameSlug, providerSlug, limit, offset) {
    const result = await AsyncGetGameReviews({
        variables: { gameSlug, providerSlug, limit, offset },
        context: { api: Api.public } // Public endpoint
    });
    if (!result.data?.getGameReviews) {
        return [];
    }
    return result.data.getGameReviews.map((review) => ({
        reviewId: review.reviewId,
        playerId: review.playerId,
        username: review.username || '',
        rating: review.rating,
        reviewText: review.reviewText || undefined,
        createdAt: review.createdAt,
        updatedAt: review.updatedAt || undefined
    }));
}
async function apiDeleteReview(reviewId) {
    const result = await DeleteReview({
        variables: { reviewId },
        context: { api: Api.player } // Requires authentication
    });
    if (!result.data?.deleteReview) {
        throw new Error('Failed to delete review');
    }
    return {
        success: result.data.deleteReview.success,
        message: result.data.deleteReview.message || ''
    };
}
async function apiGetGameReviewDistribution(gameSlug, providerSlug) {
    const result = await AsyncGetGameReviewDistribution({
        variables: { gameSlug, providerSlug },
        context: { api: Api.public }, // Public endpoint
        fetchPolicy: 'network-only' // Bypass Apollo cache, always fetch fresh data
    });
    if (!result.data?.getGameReviewDistribution) {
        return [];
    }
    return result.data.getGameReviewDistribution.map((dist) => ({
        starRating: String(dist.starRating),
        reviewCount: dist.reviewCount,
        percentage: String(dist.percentage)
    }));
}
async function apiGetTopReviewedGames(minRating, limit) {
    const result = await AsyncGetTopReviewedGames({
        variables: { minRating, limit },
        context: { api: Api.public } // Public endpoint
    });
    if (!result.data?.getTopReviewedGames) {
        return [];
    }
    return result.data.getTopReviewedGames.map((game) => ({
        gameSlug: game.gameSlug,
        providerSlug: game.providerSlug,
        averageRating: String(game.averageRating),
        totalReviews: game.totalReviews
    }));
}
// ============================================================================
// EXPORTED API
// ============================================================================
export async function createOrUpdateReview(input) {
    if (USE_MOCK) {
        return mockCreateOrUpdateReview(input);
    }
    return apiCreateOrUpdateReview(input);
}
export async function getGameReviews(gameSlug, providerSlug, limit, offset) {
    if (USE_MOCK) {
        return mockGetGameReviews(gameSlug, providerSlug, limit, offset);
    }
    return apiGetGameReviews(gameSlug, providerSlug, limit, offset);
}
export async function deleteReview(reviewId) {
    if (USE_MOCK) {
        return mockDeleteReview(reviewId);
    }
    return apiDeleteReview(reviewId);
}
export async function getGameReviewDistribution(gameSlug, providerSlug) {
    if (USE_MOCK) {
        return mockGetGameReviewDistribution(gameSlug, providerSlug);
    }
    return apiGetGameReviewDistribution(gameSlug, providerSlug);
}
export async function getTopReviewedGames(minRating, limit) {
    if (USE_MOCK) {
        return mockGetTopReviewedGames(minRating, limit);
    }
    return apiGetTopReviewedGames(minRating, limit);
}
